<template>
  <div class="fg-card">
    <div class="fg-card__head">
      <div class="fg-card__title">{{ $t('compareFinancialGroup') }}</div>
    </div>
    <div
      v-if="canSee"
      class="fg-card__body"
    >
      <div
        v-if="hasData"
        class="fg-card__legend"
      >
        <div
          v-for="(row, index) in legendRows"
          :key="row.name"
          class="fg-card__legend-item"
        >
          <span
            class="fg-card__swatch"
            :style="{ background: palette[index % palette.length] }"
          />
          <span class="fg-card__legend-name">{{ row.name }}</span>
        </div>
      </div>
      <div
        v-if="hasData"
        class="fg-card__chart"
      >
        <apexchart
          type="line"
          height="214"
          :options="chartOptions"
          :series="series"
        />
      </div>
      <div
        v-else-if="!loading"
        class="fg-card__empty"
      >
        <home-empty-state />
      </div>
      <v-progress-circular
        v-if="loading"
        :width="3"
        color="blue"
        indeterminate
        size="22"
      />
    </div>
    <div
      v-else
      class="fg-card__empty"
    >
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

const COLORS = ['#0066FF', '#7C4DFF', '#00CAE3', '#4CAF50', '#FF9800', '#E91E63', '#FF5252', '#3B82F6']

export default {
  name: 'HomeFinancialGroups',
  components: { HomeEmptyState },
  data () {
    return {
      categories: [],
      series: [],
      palette: COLORS,
      loading: false
    }
  },
  computed: {
    canSee () {
      return this.$store.getters.getpermission && this.$store.getters.getpermission.includes('overview')
    },
    hasData () {
      return this.series.length > 0
    },
    legendRows () {
      return this.series
    },
    chartOptions () {
      const self = this
      return {
        chart: {
          type: 'line',
          fontFamily: 'IRANSans',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          parentHeightOffset: 0
        },
        colors: COLORS,
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        markers: { size: 0 },
        grid: {
          borderColor: '#E5E7EB',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { left: 8, right: 4 }
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter (value) {
              return self.$numberFormat(value) + ' ' + (self.$store.state.currency.name || '')
            }
          }
        },
        xaxis: {
          categories: this.categories,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            style: { fontSize: '10px', colors: '#6B7280' }
          }
        },
        yaxis: {
          opposite: !!this.$vuetify.rtl,
          labels: {
            style: { fontSize: '10px', colors: '#6B7280' },
            offsetX: this.$vuetify.rtl ? -8 : 0,
            formatter (value) {
              return self.$numberFormat(value)
            }
          }
        }
      }
    }
  },
  mounted () {
    this.fetchList()
  },
  methods: {
    fetchList () {
      this.loading = true
      this.$axios.get(this.$endpoint + 'widgets', {
        params: {
          type: 'compareFinancial',
          category: 'group',
          remain: 'debtorCreditor'
        }
      }).then(res => {
        const data = res.data || {}
        this.categories = data.x || []
        this.series = (data.data || []).map(item => ({
          name: item.name,
          data: (item.data || []).map(value => parseFloat(value) || 0)
        }))
      }).catch(() => {
        this.categories = []
        this.series = []
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.fg-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px;
  height: 262px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.fg-card__head {
  flex-shrink: 0;
  margin-bottom: 2px;
}
.fg-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.fg-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.fg-card__legend {
  flex: 0 0 132px;
  max-width: 132px;
  min-width: 0;
  max-height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
}
.fg-card__legend::-webkit-scrollbar {
  width: 4px;
}
.fg-card__legend::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 4px;
}
.fg-card__legend-item {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
}
.fg-card__swatch {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}
.fg-card__legend-name {
  font-size: 9px;
  line-height: 1.35;
  color: #374151;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.fg-card__chart {
  flex: 1 1 auto;
  min-width: 0;
  height: 214px;
}
.fg-card__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
}
</style>
